import React from 'react';
import { useData } from './DataContext';
import { Reveal, StaggerContainer, StaggerItem } from './Reveal';
import { Calendar, MapPin, Clock, ExternalLink } from 'lucide-react';
import { Button } from './ui/button';
import useScrambleText from '../hooks/useScrambleText';
import ElectricCard from './ElectricCard';

const Events = () => {
  const { events } = useData();
  const h2Ref = useScrambleText("Upcoming Events");
  const h3Ref = useScrambleText("Join The Journey");

  // Sort events by date
  const sortedEvents = [...events].sort((a, b) => new Date(a.date) - new Date(b.date));

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      month: 'long', 
      day: 'numeric', 
      year: 'numeric' 
    });
  };

  return (
    <section id="events" className="py-24 px-6 relative bg-background">
      <div className="max-w-7xl mx-auto">
        <Reveal>
          <div className="text-center mb-16">
            <h2 ref={h2Ref} className="text-primary font-heading text-sm tracking-[0.3em] uppercase mb-4 cursor-pointer">Upcoming Events</h2>
            <h3 ref={h3Ref} className="text-4xl md:text-5xl font-heading font-bold korov-gradient-text cursor-pointer">Join The Journey</h3>
          </div>
        </Reveal>

        <StaggerContainer className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {sortedEvents.length > 0 ? (
            sortedEvents.map((event) => (
              <StaggerItem key={event.id} className="group">
                <ElectricCard color="#00ff41">
                  <div className="flex flex-col h-full">
                    {/* Event Image */}
                    <div className="relative h-48 overflow-hidden">
                      <img 
                        src={event.imageUrl} 
                        alt={event.title} 
                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-card to-transparent opacity-60" />
                    </div>

                    {/* Event Info */}
                    <div className="p-6 bg-card/50 backdrop-blur-sm flex-1 flex flex-col">
                      <h4 className="text-2xl font-heading font-bold text-gradient-platinum mb-4 group-hover:text-primary group-hover:text-glow transition-all duration-300">
                        {event.title}
                      </h4>

                      <div className="space-y-3 mb-4 flex-1">
                        <div className="flex items-center gap-3 text-muted-foreground">
                          <Calendar className="w-4 h-4 text-primary" />
                          <span>{formatDate(event.date)}</span>
                        </div>
                        
                        <div className="flex items-center gap-3 text-muted-foreground">
                          <Clock className="w-4 h-4 text-primary" />
                          <span>{event.time}</span>
                        </div>

                        <div className="flex items-center gap-3 text-muted-foreground">
                          <MapPin className="w-4 h-4 text-primary" />
                          <span>{event.venue}</span>
                        </div>

                        <p className="text-sm text-muted-foreground mt-4 leading-relaxed">
                          {event.description}
                        </p>
                      </div>

                      {event.ticketLink && event.ticketLink !== '#' && (
                        <Button 
                          className="w-full bg-primary text-black hover:bg-primary/80 hover:shadow-[0_0_20px_hsl(var(--primary)/0.4)] font-heading tracking-wider transition-all duration-300"
                          onClick={() => window.open(event.ticketLink, '_blank')}
                        >
                          GET TICKETS <ExternalLink className="w-4 h-4 ml-2" />
                        </Button>
                      )}
                    </div>
                  </div>
                </ElectricCard>
              </StaggerItem>
            ))
          ) : (
            <div className="col-span-full text-center py-12">
              <p className="text-muted-foreground text-lg">
                No upcoming events at the moment. Stay tuned!
              </p>
            </div>
          )}
        </StaggerContainer>
      </div>
    </section>
  );
};

export default Events;
